<template>
	<VCard
		ref="athleteTileItem"
		color="background"
		:border="'sm opacity-10'"
		data-testid="elementAthleteCard"
	>
		<div class="d-flex justify-between align-start justify-space-between px-4 pt-1">
			<div class="pt-3">
				<CommonAvatar
					:src="athlete.profileImage"
					:size="64"
					:alt="athlete.fullName"
				/>
			</div>
			<div class="d-flex align-start ga-2 pt-1 mr-n1">
				<VBtn
					v-for="socialProvider in athlete.socialProfiles"
					:key="socialProvider.platform?.name"
					class="athlete-tile__social-icon"
					rel="noopener noreferrer"
					target="_blank"
					variant="text"
					icon
					size="2rem"
					:href="socialProvider.url"
					:aria-label="`${athlete.fullName} ${socialProvider.platform?.name}  ${t('profile',1)}`"
				>
					<VIcon size="1.25rem">
						{{ socialProvider.platform?.icon }}
					</VIcon>
				</VBtn>
				<VBtn
					v-for="link in links"
					:key="link.url"
					variant="text"
					icon
					size="2rem"
					:href="link.url"
					rel="noopener noreferrer"
					target="_blank"
					class="text-decoration-none"
					data-testid="link"
				>
					<VIcon size="1.25rem">
						link
					</VIcon>
				</VBtn>
			</div>
		</div>
		<div class="athlete-tile__body">
			<VCardText class="py-0 text-body-2 text-medium-emphasis athlete-tile__text ">
				<span
					class="athlete-tile__name text-high-emphasis text-h4"
					data-testid="elementName"
				>{{ athlete.fullName }}</span>
				<div
					class="athlete-tile__team"
				>
					{{ athlete.teamName }}
				</div>
				<div
					v-if="athlete.rateCardsCount"
					class="athlete-tile__ratecards text-success pt-2"
					data-testid="elementOpportunities"
				>
					<VIcon
						size="1rem"
					>
						wallet
					</VIcon>
					{{ t('opportunitiesAvailable', athlete.rateCardsCount ) }}
				</div>
			</VCardText>
		</div>
		<VCardActions class="athlete-tile__footer">
			<VBtnSchool
				block
				data-testid="buttonWorkWithMe"
				@click.stop="$emit('work-with-me', athlete)"
			>
				{{ t('workWithMe') }}
			</VBtnSchool>
		</VCardActions>
	</VCard>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { type SingleExchangeSchoolSiteAthlete } from '@/types/exchangeSchoolSite';
import { CommonAvatar } from '@teamworksdev/vuetify';
import { useI18n } from 'vue-i18n';

const props = withDefaults(defineProps<{
	athlete?: SingleExchangeSchoolSiteAthlete;
}>(), {
	athlete: () => ({
		uuid: '',
		firstName: '',
		lastName: '',
		fullName: '',
		profileImage: '',
		teamName: '',
		rateCardsCount: 0,
		socialProfiles: [],
		links: [],
	}),
});

defineEmits<{
	(e: 'work-with-me', payload: SingleExchangeSchoolSiteAthlete): void;
}>();

const { t } = useI18n();

const links = computed(() => {
	return props.athlete.links.length ? props.athlete.links.slice(0, 2) : [];
});
</script>

<style lang="scss" scoped>
@use 'sass:map';
@use '@/styles/settings.scss';
.athlete-tile{
		&__body{
			margin-top:calc(settings.$card-text-padding/2);
			height:calc(
				(map.get(settings.$typography,'h4','size') * map.get(settings.$typography,'h4','line-height') * 2) +
				(map.get(settings.$typography,'body-2','size') * map.get(settings.$typography,'body-2','line-height') * 3)
			);
		}
		&__name--clickable {
				cursor: pointer;
				&:hover{
					text-decoration: underline;
				}
		}
		&__ratecards, &__team{
			display: -webkit-box;
			-webkit-line-clamp: 2;
			-webkit-box-orient: vertical;
			overflow: hidden;
		}
		&__footer{
			padding:settings.$card-text-padding;
		}
	}
</style>

